/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { later } from '@ember/runloop';
// Internal Imports
import config from 'linced-app/config/environment';
import Scope from '@linc-technologies/linced-common/enums/scopes';
import SyncStep from '@linc-technologies/linced-common/enums/sif-au-sync-step';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
// External Imports
import moment from 'moment';

export default class SifAuController extends Controller {
  // Services
  @service
  fetch;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Scope = Scope;

  SyncStep = SyncStep;

  @computed('model.connections.{length,@each.syncEnabled}')
  get syncEnabled() {
    return (
      this.get('model.connections.length') === 1 &&
      this.get('model.connections.0.syncEnabled')
    );
  }

  @computed('syncEnabled', 'model.connections.@each.periodicSyncEnabled')
  get periodicSyncEnabled() {
    return (
      this.syncEnabled && this.get('model.connections.0.periodicSyncEnabled')
    );
  }

  @tracked
  staffSyncing = false;

  @tracked
  studentsSyncing = false;

  @tracked
  datesSyncing = false;

  @tracked
  syncGroupStaff = false;

  @action
  setSyncGroupStaff(value) {
    this.syncGroupStaff = value;
  }

  @tracked
  groupsSyncing = false;

  @tracked
  locationsSyncing = false;

  @computed
  get years() {
    let currentYear = moment().year();
    let nextYear = currentYear + 1;
    let firstYear = currentYear - 5;
    let years = [];
    for (let year = firstYear; year <= nextYear; year++) {
      years.push(year);
    }
    return years;
  }

  @tracked
  startYear = moment().year();

  @action
  setStartYear(value) {
    this.startYear = value;
  }

  @tracked
  endYear = moment().year();

  @action
  setEndYear(value) {
    this.endYear = value;
  }

  @tracked
  connectionDialogOpen = false;

  @tracked
  saveInProgress;

  @tracked connection;

  @action
  openConnectionDialog() {
    // Ember get() has no [0] path syntax.
    let connection = this.model.connections[0];
    if (isEmpty(connection)) {
      // No existing connection - create one.
      connection = this.store.createRecord('sif/au/connection', {
        schoolId: this.get('session.school'),
      });
    }
    this.connection = connection;
    this.connectionDialogOpen = true;
  }

  @action
  addGroup(group) {
    this.connection.limitToGroups.pushObject(group);
  }

  @action
  removeGroup(group) {
    this.connection.limitToGroups.removeObject(group);
  }

  @action
  closeConnectionDialog() {
    rollbackRecord(this.connection);
    this.connectionDialogOpen = false;
  }

  @action
  toggleSyncStep(step, enable) {
    if (enable) {
      this.connection.periodicSyncSteps.pushObject(step.value);
    } else {
      this.connection.periodicSyncSteps.removeObject(step.value);
    }
  }

  @action
  saveConnection() {
    this.saveInProgress = true;
    this.connection
      .save()
      .then(() => {
        this.toaster.success('Connection saved successfully.');
        this.connectionDialogOpen = false;
        this.model.connections.update();
      })
      .catch(() => {
        this.toaster.error('Error saving connection!');
      })
      .finally(() => {
        this.saveInProgress = false;
      });
  }

  @action
  syncStudents() {
    this.studentsSyncing = true;
    let url = `${config.APP.apiHost}/providers/au/sif/${config.APP.apiPath}/learners/:sync`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then((resp) => {
        if (!isEmpty(resp.bulkImportId)) {
          // We got a bulk import ID directly.  Go to its logs.
          this.router.transitionTo(
            'admin.import-logs.import',
            resp.bulkImportId,
          );
        } else {
          // Use the new process monitor workflow.
          this.router.transitionTo('admin.import.sif-logs', {
            queryParams: { processMonitorId: resp.processMonitorId },
          });
        }
      })
      .catch(() => {
        this.toaster.error(`Error synchronising students!`);
      })
      .finally(() => {
        this.studentsSyncing = false;
      });
  }

  @action
  syncStaff() {
    this.staffSyncing = true;
    let url = `${config.APP.apiHost}/providers/au/sif/${config.APP.apiPath}/staff/:sync`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then((resp) => {
        if (!isEmpty(resp.bulkImportId)) {
          // We got a bulk import ID directly.  Go to its logs.
          this.router.transitionTo(
            'admin.import-logs.import',
            resp.bulkImportId,
          );
        } else {
          // Use the new process monitor workflow.
          this.router.transitionTo('admin.import.sif-logs', {
            queryParams: { processMonitorId: resp.processMonitorId },
          });
        }
      })
      .catch(() => {
        this.toaster.error(`Error synchronising staff!`);
      })
      .finally(() => {
        this.staffSyncing = false;
      });
  }

  @action
  syncGroups() {
    this.groupsSyncing = true;
    let url = `${config.APP.apiHost}/providers/au/sif/${config.APP.apiPath}/groups/:sync`;
    let data = {
      schoolId: this.session.activeSchool,
      syncStaff: this.syncGroupStaff,
    };
    return this.fetch
      .post(url, { data })
      .then((resp) => {
        return this.store
          .query('group', { year: moment().year() })
          .finally(() => {
            this.router.transitionTo('admin.import.sif-logs', {
              queryParams: { processMonitorId: resp.processMonitorId },
            });
          });
      })
      .catch(() => {
        this.toaster.error(`Error synchronising groups!`);
      })
      .finally(() => {
        this.groupsSyncing = false;
      });
  }

  @action
  syncDates() {
    this.datesSyncing = true;

    let url = `${config.APP.apiHost}/providers/au/sif/${config.APP.apiPath}/dates/:sync`;

    let years = [];
    for (let year = this.startYear; year <= this.endYear; year++) {
      years.push(year);
    }

    let chain = years.reduce((chain, year) => {
      let data = {
        schoolId: this.session.activeSchool,
        academicYear: year,
      };
      return chain
        .then(() => {
          return this.fetch.post(url, { data });
        })
        .then(() => {
          this.toaster.success(`${year} dates synchronised successfully!`);
        })
        .catch(() => {
          this.toaster.error(`Error synchronising ${year} dates!`);
        });
    }, Promise.resolve());

    return chain.finally(() => {
      this.datesSyncing = false;
    });
  }

  @action
  syncLocations() {
    this.locationsSyncing = true;
    let url = `${config.APP.apiHost}/providers/au/sif/${config.APP.apiPath}/locations/:sync`;
    let data = {
      schoolId: this.session.activeSchool,
    };
    return this.fetch
      .post(url, { data })
      .then(() => {
        later(() => {
          this.locationsSyncing = false;
          this.toaster.success(`Locations synchronised successfully!`);
        }, 5000);
      })
      .catch(() => {
        this.locationsSyncing = false;
        this.toaster.error(`Error synchronising locations!`);
      });
  }
}
